/**
 * Downtime, rejects, MTBF and faults of a line: the machine statements of the
 * machine view, asked for every machine of the line and shown machine by
 * machine in line order (see lineStations.ts). Same sub-tabs as the machine
 * view. How the line KPIs are calculated is in the help ("KPIs of a line").
 */

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SubTabNav from "../common/SubTabNav";
import { useThemedCharts } from "../../hooks/useThemedCharts";
import { useThemeClasses } from "../../providers/ThemeProvider";
import { ChartDataService, type ChartRequestParams } from "../../services/ChartDataService";
import { cx } from "../../styles";
import { LineShiftsChart, LineStationsChart, type StationStack } from "./LineCharts";
import {
  downtimeOf,
  totalsByDateShift,
  totalsByShiftCategory,
  totalsOfFaultRows,
  valuesPerShift,
  type LineStation,
  type NamedValue,
  type Totals,
} from "./lineStations";
import { MAX_LINE_MACHINES, useLineFanout, type FanoutState } from "./useLineData";

export interface LineTabProps {
  lineId: string;
  /** The line's machines with data, in line order. */
  stations: LineStation[];
  shifts: string[];
}

const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;
/** Statuses, speed ranges shown as their own stack; the rest is "Other". */
const MAX_STACKS = 7;

// Static methods use `this`, so they are wrapped once (stable references).
const fetchers = {
  downtime: (p: ChartRequestParams, token: string) => ChartDataService.fetchDowntime(p, token),
  rejectRatio: (p: ChartRequestParams, token: string) => ChartDataService.fetchRejectRatio(p, token),
  rejects: (p: ChartRequestParams, token: string) => ChartDataService.fetchNumberOfRejects(p, token),
  rejectEvents: (p: ChartRequestParams, token: string) => ChartDataService.fetchNumberOfRejectEvents(p, token),
  mtbf: (p: ChartRequestParams, token: string) => ChartDataService.fetchMTBF(p, token),
  speed: (p: ChartRequestParams, token: string) => ChartDataService.fetchAverageProductionSpeed(p, token),
  faults: (p: ChartRequestParams, token: string) => ChartDataService.fetchNumberOfFaults(p, token),
  faultDuration: (p: ChartRequestParams, token: string) =>
    ChartDataService.fetchDowntimeDurationOfFaults(p, token),
  status: (p: ChartRequestParams, token: string) => ChartDataService.fetchMachineStatusPerShift(p, token),
};

const STATUS_PARAMS = { agg_type: "number" };

/** The machines asked: the first MAX_LINE_MACHINES of the line. */
const useAsked = (stations: LineStation[]) =>
  useMemo(() => stations.slice(0, MAX_LINE_MACHINES), [stations]);

const useSns = (asked: LineStation[]) => useMemo(() => asked.map((s) => s.sn), [asked]);

const scaled = (parts: NamedValue[], factor: number): NamedValue[] =>
  parts.map((p) => ({ name: p.name, value: p.value / factor }));

/** What the chart cannot show: machines beyond the limit, machines that did not answer. */
const LineTabWarnings: React.FC<{
  stations: LineStation[];
  asked: LineStation[];
  fanout: FanoutState;
}> = ({ stations, asked, fanout }) => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();
  const failed = fanout.loading ? [] : asked.filter((s) => fanout.errors.has(s.sn));
  const tooMany = stations.length > asked.length;
  if (!tooMany && failed.length === 0) return null;
  return (
    <div className={cx("text-xs space-y-1 mb-3", themeClasses.textWarning)}>
      {tooMany && <p>{t("lineKpi.tooMany", { count: asked.length, total: stations.length })}</p>}
      {failed.length > 0 && (
        <p>
          {t("lineKpi.failed", {
            machines: failed.map((s) => `${s.label} (${fanout.errors.get(s.sn)})`).join(", "),
          })}
        </p>
      )}
    </div>
  );
};

/**
 * One stack per part name (status, speed range) over all machines: the
 * MAX_STACKS largest, the rest as "Other".
 */
function stacksByPart(
  asked: LineStation[],
  totals: Map<string, Totals>,
  factor: number,
  otherLabel: string,
  colorOf: (i: number) => string,
): { stacks: StationStack[]; details: Map<string, NamedValue[]> } {
  const sum = new Map<string, number>();
  for (const tot of totals.values()) {
    for (const p of tot.parts) sum.set(p.name, (sum.get(p.name) ?? 0) + p.value);
  }
  const names = [...sum.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n);
  const shown = names.slice(0, MAX_STACKS);
  const rest = new Set(names.slice(MAX_STACKS));

  const stacks: StationStack[] = shown.map((name, i) => ({
    name,
    color: colorOf(i),
    values: new Map(
      asked
        .filter((s) => totals.has(s.sn))
        .map((s) => [s.sn, (totals.get(s.sn)!.parts.find((p) => p.name === name)?.value ?? 0) / factor]),
    ),
  }));
  if (rest.size) {
    stacks.push({
      name: otherLabel,
      color: "#94a3b8",
      values: new Map(
        asked
          .filter((s) => totals.has(s.sn))
          .map((s) => [
            s.sn,
            totals
              .get(s.sn)!
              .parts.filter((p) => rest.has(p.name))
              .reduce((acc, p) => acc + p.value, 0) / factor,
          ]),
      ),
    });
  }
  const details = new Map([...totals].map(([sn, tot]) => [sn, scaled(tot.parts, factor)]));
  return { stacks, details };
}

// ---------------------------------------------------------------- downtime

export const LineDowntimeTab: React.FC<LineTabProps> = ({ lineId, stations, shifts }) => {
  const { t } = useTranslation();
  const asked = useAsked(stations);
  const fanout = useLineFanout("downtime_accumulated", useSns(asked), fetchers.downtime, shifts);

  const { stacks, details } = useMemo(() => {
    const own = new Map<string, number>();
    const waiting = new Map<string, number>();
    const details = new Map<string, NamedValue[]>();
    for (const s of asked) {
      if (!fanout.data.has(s.sn)) continue;
      const d = downtimeOf(fanout.data.get(s.sn));
      own.set(s.sn, d.own.total / HOUR_MS);
      waiting.set(s.sn, d.waiting.total / HOUR_MS);
      details.set(
        s.sn,
        scaled([...d.own.parts, ...d.waiting.parts].sort((a, b) => b.value - a.value), HOUR_MS),
      );
    }
    return {
      stacks: [
        { name: t("lineKpi.ownStops"), color: "#ef4444", values: own },
        { name: t("lineKpi.waiting"), color: "#f59e0b", values: waiting },
      ],
      details,
    };
  }, [asked, fanout.data, t]);

  return (
    <div>
      <LineTabWarnings stations={stations} asked={asked} fanout={fanout} />
      <LineStationsChart
        lineId={lineId}
        stations={asked}
        stacks={stacks}
        details={details}
        unit=" h"
        yTitle={t("lineKpi.hours")}
        loading={fanout.loading}
        noDataMessage={t("lineKpi.noData")}
        filename="line-downtime"
      />
    </div>
  );
};

// ----------------------------------------------------------------- rejects

const LineRejectRatio: React.FC<LineTabProps> = ({ lineId, stations, shifts }) => {
  const { t } = useTranslation();
  const asked = useAsked(stations);
  const fanout = useLineFanout("reject_ratio", useSns(asked), fetchers.rejectRatio, shifts);
  const values = useMemo(
    () =>
      new Map(
        asked
          .filter((s) => fanout.data.has(s.sn))
          .map((s) => [s.sn, valuesPerShift(fanout.data.get(s.sn), shifts, "reject_rate")]),
      ),
    [asked, fanout.data, shifts],
  );
  return (
    <div>
      <LineTabWarnings stations={stations} asked={asked} fanout={fanout} />
      <LineShiftsChart
        lineId={lineId}
        stations={asked}
        values={values}
        unit="%"
        decimals={2}
        yTitle={t("lineKpi.rejectRatio")}
        loading={fanout.loading}
        noDataMessage={t("lineKpi.noData")}
        filename="line-reject-ratio"
        commentChartType="line_reject_ratio"
      />
    </div>
  );
};

const LineRejectCounts: React.FC<LineTabProps & { events: boolean }> = ({
  lineId,
  stations,
  shifts,
  events,
}) => {
  const { t } = useTranslation();
  const asked = useAsked(stations);
  const fanout = useLineFanout(
    events ? "number_of_reject_events" : "number_of_rejects",
    useSns(asked),
    events ? fetchers.rejectEvents : fetchers.rejects,
    shifts,
  );
  const { stacks, details } = useMemo(() => {
    const values = new Map<string, number>();
    const details = new Map<string, NamedValue[]>();
    for (const s of asked) {
      if (!fanout.data.has(s.sn)) continue;
      const tot = totalsByDateShift(fanout.data.get(s.sn), shifts);
      values.set(s.sn, tot.total);
      details.set(s.sn, tot.parts);
    }
    return { stacks: [{ name: t(events ? "lineKpi.subTabs.events" : "lineKpi.subTabs.rejects"), values }], details };
  }, [asked, fanout.data, shifts, events, t]);
  return (
    <div>
      <LineTabWarnings stations={stations} asked={asked} fanout={fanout} />
      <LineStationsChart
        lineId={lineId}
        stations={asked}
        stacks={stacks}
        details={details}
        decimals={0}
        yTitle={t("lineKpi.count")}
        loading={fanout.loading}
        noDataMessage={t("lineKpi.noData")}
        filename={events ? "line-reject-events" : "line-rejects"}
      />
    </div>
  );
};

export const LineRejectsTab: React.FC<LineTabProps> = (props) => {
  const { t } = useTranslation();
  const [sub, setSub] = useState<"ratio" | "rejects" | "events">("ratio");
  return (
    <div className="space-y-4">
      <SubTabNav
        tabs={(["ratio", "rejects", "events"] as const).map((key) => ({
          key,
          label: t(`lineKpi.subTabs.${key}`),
        }))}
        activeTab={sub}
        onTabChange={(k) => setSub(k as typeof sub)}
      />
      {sub === "ratio" ? (
        <LineRejectRatio {...props} />
      ) : (
        <LineRejectCounts {...props} events={sub === "events"} />
      )}
    </div>
  );
};

// -------------------------------------------------------------------- MTBF

const LineMtbf: React.FC<LineTabProps> = ({ lineId, stations, shifts }) => {
  const { t } = useTranslation();
  const asked = useAsked(stations);
  const fanout = useLineFanout("mtbf", useSns(asked), fetchers.mtbf, shifts);
  const values = useMemo(
    () =>
      new Map(
        asked
          .filter((s) => fanout.data.has(s.sn))
          .map((s) => {
            const perShift = valuesPerShift(fanout.data.get(s.sn), shifts, "mtbf");
            // ms → minutes; a shift without a stop has no MTBF (0 from the statement)
            const minutes = new Map(
              [...perShift].filter(([, v]) => v > 0).map(([c, v]) => [c, v / MINUTE_MS]),
            );
            return [s.sn, minutes] as const;
          }),
      ),
    [asked, fanout.data, shifts],
  );
  return (
    <div>
      <LineTabWarnings stations={stations} asked={asked} fanout={fanout} />
      <LineShiftsChart
        lineId={lineId}
        stations={asked}
        values={values}
        unit=" min"
        decimals={0}
        yTitle={t("lineKpi.minutes")}
        loading={fanout.loading}
        noDataMessage={t("lineKpi.noData")}
        filename="line-mtbf"
        commentChartType="line_mtbf"
      />
    </div>
  );
};

/** Speed ranges ("1 - 200") by their lower bound. */
const byRangeStart = (a: string, b: string) => (parseFloat(a) || 0) - (parseFloat(b) || 0);

const LineSpeed: React.FC<LineTabProps> = ({ lineId, stations, shifts }) => {
  const { t } = useTranslation();
  const { getSeriesColor } = useThemedCharts();
  const asked = useAsked(stations);
  const fanout = useLineFanout("average_production_speed", useSns(asked), fetchers.speed, shifts);
  const { stacks, details } = useMemo(() => {
    const totals = new Map(
      asked
        .filter((s) => fanout.data.has(s.sn))
        .map((s) => [s.sn, totalsByShiftCategory(fanout.data.get(s.sn), shifts)] as const),
    );
    const result = stacksByPart(asked, totals, HOUR_MS, t("lineKpi.other"), getSeriesColor);
    // speed ranges read best from slow to fast
    const other = result.stacks.filter((s) => s.name === t("lineKpi.other"));
    const ranges = result.stacks.filter((s) => s.name !== t("lineKpi.other"));
    ranges.sort((a, b) => byRangeStart(a.name, b.name));
    return { stacks: [...ranges, ...other], details: result.details };
  }, [asked, fanout.data, shifts, t, getSeriesColor]);
  return (
    <div>
      <LineTabWarnings stations={stations} asked={asked} fanout={fanout} />
      <LineStationsChart
        lineId={lineId}
        stations={asked}
        stacks={stacks}
        details={details}
        unit=" h"
        yTitle={t("lineKpi.hours")}
        loading={fanout.loading}
        noDataMessage={t("lineKpi.noData")}
        filename="line-speed"
      />
    </div>
  );
};

export const LineMTBFTab: React.FC<LineTabProps> = (props) => {
  const { t } = useTranslation();
  const [sub, setSub] = useState<"mtbf" | "speed">("mtbf");
  return (
    <div className="space-y-4">
      <SubTabNav
        tabs={(["mtbf", "speed"] as const).map((key) => ({ key, label: t(`lineKpi.subTabs.${key}`) }))}
        activeTab={sub}
        onTabChange={(k) => setSub(k as typeof sub)}
      />
      {sub === "mtbf" ? <LineMtbf {...props} /> : <LineSpeed {...props} />}
    </div>
  );
};

// ------------------------------------------------------------------ faults

const LineFaultCounts: React.FC<LineTabProps> = ({ lineId, stations, shifts }) => {
  const { t } = useTranslation();
  const asked = useAsked(stations);
  const fanout = useLineFanout("number_of_faults", useSns(asked), fetchers.faults, shifts);
  const { stacks, details } = useMemo(() => {
    const values = new Map<string, number>();
    const details = new Map<string, NamedValue[]>();
    for (const s of asked) {
      if (!fanout.data.has(s.sn)) continue;
      const tot = totalsOfFaultRows(fanout.data.get(s.sn));
      values.set(s.sn, tot.total);
      details.set(s.sn, tot.parts);
    }
    return { stacks: [{ name: t("lineKpi.subTabs.number"), values }], details };
  }, [asked, fanout.data, t]);
  return (
    <div>
      <LineTabWarnings stations={stations} asked={asked} fanout={fanout} />
      <LineStationsChart
        lineId={lineId}
        stations={asked}
        stacks={stacks}
        details={details}
        decimals={0}
        yTitle={t("lineKpi.count")}
        loading={fanout.loading}
        noDataMessage={t("lineKpi.noData")}
        filename="line-faults"
      />
    </div>
  );
};

const LineFaultDuration: React.FC<LineTabProps> = ({ lineId, stations, shifts }) => {
  const { t } = useTranslation();
  const asked = useAsked(stations);
  const fanout = useLineFanout("downtime_duration_of_faults", useSns(asked), fetchers.faultDuration, shifts);
  const { stacks, details } = useMemo(() => {
    const values = new Map<string, number>();
    const details = new Map<string, NamedValue[]>();
    for (const s of asked) {
      if (!fanout.data.has(s.sn)) continue;
      const tot = totalsByShiftCategory(fanout.data.get(s.sn), shifts);
      values.set(s.sn, tot.total / HOUR_MS);
      details.set(s.sn, scaled(tot.parts, HOUR_MS));
    }
    return { stacks: [{ name: t("lineKpi.subTabs.duration"), color: "#ef4444", values }], details };
  }, [asked, fanout.data, shifts, t]);
  return (
    <div>
      <LineTabWarnings stations={stations} asked={asked} fanout={fanout} />
      <LineStationsChart
        lineId={lineId}
        stations={asked}
        stacks={stacks}
        details={details}
        unit=" h"
        yTitle={t("lineKpi.hours")}
        loading={fanout.loading}
        noDataMessage={t("lineKpi.noData")}
        filename="line-fault-downtime"
      />
    </div>
  );
};

const LineStatus: React.FC<LineTabProps> = ({ lineId, stations, shifts }) => {
  const { t } = useTranslation();
  const { getSeriesColor } = useThemedCharts();
  const asked = useAsked(stations);
  const fanout = useLineFanout("machine_status_per_shift", useSns(asked), fetchers.status, shifts, STATUS_PARAMS);
  const { stacks, details } = useMemo(() => {
    const totals = new Map(
      asked
        .filter((s) => fanout.data.has(s.sn))
        .map((s) => [s.sn, totalsByShiftCategory(fanout.data.get(s.sn), shifts)] as const),
    );
    return stacksByPart(asked, totals, HOUR_MS, t("lineKpi.other"), getSeriesColor);
  }, [asked, fanout.data, shifts, t, getSeriesColor]);
  return (
    <div>
      <LineTabWarnings stations={stations} asked={asked} fanout={fanout} />
      <LineStationsChart
        lineId={lineId}
        stations={asked}
        stacks={stacks}
        details={details}
        unit=" h"
        yTitle={t("lineKpi.hours")}
        loading={fanout.loading}
        noDataMessage={t("lineKpi.noData")}
        filename="line-status"
      />
    </div>
  );
};

export const LineFaultsTab: React.FC<LineTabProps> = (props) => {
  const { t } = useTranslation();
  const [sub, setSub] = useState<"number" | "duration" | "status">("number");
  return (
    <div className="space-y-4">
      <SubTabNav
        tabs={(["number", "duration", "status"] as const).map((key) => ({
          key,
          label: t(`lineKpi.subTabs.${key}`),
        }))}
        activeTab={sub}
        onTabChange={(k) => setSub(k as typeof sub)}
      />
      {sub === "number" ? (
        <LineFaultCounts {...props} />
      ) : sub === "duration" ? (
        <LineFaultDuration {...props} />
      ) : (
        <LineStatus {...props} />
      )}
    </div>
  );
};
